import { pgTable, text, boolean, timestamp, integer, index, foreignKey, numeric, uniqueIndex, jsonb } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { accessTargetType } from './enums';
import { file } from './files';

/**
 * SubscriptionPlan - План абонемента
 * Отдельная сущность для гибкого управления абонементами
 */
export const subscriptionPlan = pgTable('SubscriptionPlan', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Уникальный код плана (для API и ссылок)
  code: text().notNull(),

  // Ценообразование
  price: numeric({ precision: 12, scale: 2 }).notNull(),
  currency: text().default('UZS').notNull(),

  // Мультивалютное ценообразование
  prices: jsonb().$type<Record<string, number>>(), // { "USD": 99.99, "UZS": 1200000, "RUB": 9000 }
  baseCurrency: text().default('UZS'), // Основная валюта для расчётов

  // Период продаж (когда можно купить)
  saleStartDate: timestamp({ precision: 3, mode: 'date' }),
  saleEndDate: timestamp({ precision: 3, mode: 'date' }),

  // Срок действия абонемента
  durationDays: integer(), // Относительная длительность (N дней от покупки)
  validUntil: timestamp({ precision: 3, mode: 'date' }), // Абсолютная дата окончания (приоритет над durationDays)

  // Семейный план
  isFamily: boolean().default(false).notNull(),
  familySlots: integer().default(1).notNull(), // Минимум 1 (владелец)

  // Статус
  isActive: boolean().default(true).notNull(),
  order: integer().default(0).notNull(),

  imageId: integer(),
  bannerImageId: integer(),

  // Timestamps
  deletedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  foreignKey({ columns: [table.imageId], foreignColumns: [file.id], name: 'SubscriptionPlan_imageId_fkey' }).onUpdate('cascade').onDelete('set null'),
  foreignKey({ columns: [table.bannerImageId], foreignColumns: [file.id], name: 'SubscriptionPlan_bannerImageId_fkey' }).onUpdate('cascade').onDelete('set null'),
  uniqueIndex('SubscriptionPlan_code_key').using('btree', table.code),
  index('SubscriptionPlan_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('SubscriptionPlan_order_idx').using('btree', table.order.asc().nullsLast()),
  index('SubscriptionPlan_saleStartDate_idx').using('btree', table.saleStartDate.asc().nullsLast()),
  index('SubscriptionPlan_saleEndDate_idx').using('btree', table.saleEndDate.asc().nullsLast()),
  index('SubscriptionPlan_deletedAt_idx').using('btree', table.deletedAt.desc().nullsLast()),
]);

/**
 * SubscriptionPlanEntitlement - Права доступа, которые даёт план
 * Связывает план с курсами, каналами, событиями
 */
export const subscriptionPlanEntitlement = pgTable('SubscriptionPlanEntitlement', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  subscriptionPlanId: integer().notNull(),

  // Тип и ID целевого объекта
  targetType: accessTargetType().notNull(), // COURSE, CHANNEL, EVENT, CONTENT
  targetId: integer(), // null = все объекты данного типа

  // Длительность доступа к этому объекту
  accessDurationDays: integer(), // null = до конца подписки

  // Статус
  isActive: boolean().default(true).notNull(),
  order: integer().default(0).notNull(),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  index('SubscriptionPlanEntitlement_planId_idx').using('btree', table.subscriptionPlanId.asc().nullsLast()),
  index('SubscriptionPlanEntitlement_targetType_idx').using('btree', table.targetType.asc().nullsLast()),
  foreignKey({
    columns: [table.subscriptionPlanId],
    foreignColumns: [subscriptionPlan.id],
    name: 'SubscriptionPlanEntitlement_planId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// Note: SubscriptionPlanProduct table has been removed.
// SubscriptionPlan is now directly purchasable through OrderItem with targetType='SUBSCRIPTION_PLAN'
